import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import MandatoryCGVOverlay from './components/MandatoryCGVOverlay';
import ScrollToTop from './components/ScrollToTop';
import OnboardingPopup from './components/OnboardingPopup';
import { lazy, Suspense, Component } from 'react';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { ProvinceProvider } from './context/ProvinceContext';
import { SiteSettingsProvider } from './context/SiteSettingsContext';
import Layout from './components/Layout';

// Lazy-load all pages for better performance
const HomePage = lazy(() => import('./pages/HomePage'));
const AuthPage = lazy(() => import('./pages/AuthPage'));
const ProductsPage = lazy(() => import('./pages/ProductsPage'));
const DeliveryMapPage = lazy(() => import('./pages/DeliveryMapPage'));
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage'));
const ProducersPage = lazy(() => import('./pages/ProducersPage'));
const ProducerDetailPage = lazy(() => import('./pages/ProducerDetailPage'));
const TraiteursPage = lazy(() => import('./pages/TraiteursPage'));
const TraiteurPublicPage = lazy(() => import('./pages/TraiteurPublicPage'));
const CartPage = lazy(() => import('./pages/CartPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const CheckoutSuccessPage = lazy(() => import('./pages/CheckoutSuccessPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const ReservationSuccessPage = lazy(() => import('./pages/ReservationSuccessPage'));
const ReservationBalancePage = lazy(() => import('./pages/ReservationBalancePage'));
const ReservationCancelledPage = lazy(() => import('./pages/ReservationCancelledPage'));
const AdminDashboardPage = lazy(() => import('./pages/AdminDashboardPage'));
const SellerDashboardPage = lazy(() => import('./pages/SellerDashboardPage'));
const DriverDashboardPage = lazy(() => import('./pages/DriverDashboardPage'));
const TraiteurDashboardPage = lazy(() => import('./pages/TraiteurDashboardPage'));
const CGVPage = lazy(() => import('./pages/CGVPage'));
const ShoppingListsPage = lazy(() => import('./pages/ShoppingListsPage'));
const SharedListPage = lazy(() => import('./pages/SharedListPage'));
const InspirationsPage = lazy(() => import('./pages/InspirationsPage'));
const JournalPage = lazy(() => import('./pages/JournalPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const UnsubscribePage = lazy(() => import('./pages/UnsubscribePage'));
const LegalDocumentPage = lazy(() => import('./pages/LegalDocumentPage'));
const CustomPage = lazy(() => import('./pages/CustomPage'));
const ForgotPasswordPage = lazy(() => import('./pages/PasswordResetPages').then(m => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('./pages/PasswordResetPages').then(m => ({ default: m.ResetPasswordPage })));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// Error Boundary to prevent full app crash
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, errorInfo) {
    console.error('App Error:', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      // Smart redirect: if the crash happened on the checkout flow, send the
      // user back to their cart (not to home), so they don't lose their order.
      const path = window.location.pathname;
      const isCheckoutFlow = path.startsWith('/checkout') || path.startsWith('/paiement');
      const returnUrl = isCheckoutFlow ? '/panier' : '/';
      const returnLabel = isCheckoutFlow ? 'Retour au panier' : "Retour à l'accueil";
      return (
        <div className="min-h-screen flex items-center justify-center bg-paper px-6">
          <div className="text-center max-w-md">
            <h1 className="font-serif text-3xl text-stone-text mb-4">Oups !</h1>
            <p className="text-muted-foreground font-sans mb-6">Une erreur s'est produite. Veuillez rafraîchir la page.</p>
            <button
              onClick={() => { this.setState({ hasError: false }); window.location.href = returnUrl; }}
              className="bg-brick text-white px-6 py-3 rounded-full font-sans hover:bg-brick/90 transition-colors"
            >
              {returnLabel}
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function PageLoader() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="animate-pulse font-serif text-2xl text-stone-text/20">Chargement...</div>
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ScrollToTop />
        <AuthProvider>
          <MandatoryCGVOverlay />
          <OnboardingPopup />
          <SiteSettingsProvider>
          <CartProvider>
            <ProvinceProvider>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route element={<Layout />}>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/connexion" element={<AuthPage />} />
                    <Route path="/mot-de-passe-oublie" element={<ForgotPasswordPage />} />
                    <Route path="/reinitialiser-mot-de-passe" element={<ResetPasswordPage />} />
                    <Route path="/produits" element={<ProductsPage />} />
                    <Route path="/livraison" element={<DeliveryMapPage />} />
                    <Route path="/produit/:id" element={<ProductDetailPage />} />
                    <Route path="/producteurs" element={<ProducersPage />} />
                    <Route path="/producteur/:id" element={<ProducerDetailPage />} />
                    <Route path="/plats-tout-prets" element={<TraiteursPage />} />
                    <Route path="/traiteurs" element={<Navigate to="/plats-tout-prets" replace />} />
                    <Route path="/traiteur-public/:id" element={<TraiteurPublicPage />} />
                    <Route path="/panier" element={<CartPage />} />
                    <Route path="/checkout" element={<CheckoutPage />} />
                    <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
                    <Route path="/reservation/succes" element={<ReservationSuccessPage />} />
                    <Route path="/reservation/solde/:id" element={<ReservationBalancePage />} />
                    <Route path="/reservation/annulee" element={<ReservationCancelledPage />} />
                    <Route path="/profil" element={<ProfilePage />} />
                    <Route path="/admin" element={<AdminDashboardPage />} />
                    <Route path="/vendeur" element={<SellerDashboardPage />} />
                    <Route path="/livreur" element={<DriverDashboardPage />} />
                    <Route path="/traiteur" element={<TraiteurDashboardPage />} />
                    <Route path="/cgv" element={<CGVPage />} />
                    <Route path="/mes-listes" element={<ShoppingListsPage />} />
                    <Route path="/liste/:shareToken" element={<SharedListPage />} />
                    <Route path="/inspirations" element={<InspirationsPage />} />
                    <Route path="/journal" element={<JournalPage />} />
                    <Route path="/a-propos" element={<CustomPage slug="a-propos" />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/desabonnement" element={<UnsubscribePage />} />
                    {/* Dedicated legal document URLs (RGPD compliance — each policy must be directly accessible) */}
                    <Route path="/politique-confidentialite" element={<LegalDocumentPage documentKey="rgpd" fallbackTitle="Politique de confidentialité" />} />
                    <Route path="/conditions-generales-de-vente" element={<LegalDocumentPage documentKey="cgv" fallbackTitle="Conditions Générales de Vente" />} />
                    <Route path="/conditions-generales-producteurs" element={<LegalDocumentPage documentKey="cgv_producteurs" fallbackTitle="Conditions Générales des Producteurs" />} />
                    <Route path="/mentions-legales" element={<LegalDocumentPage documentKey="identification" fallbackTitle="Mentions légales" />} />
                    <Route path="/politique-cookies" element={<LegalDocumentPage documentKey="cookies" fallbackTitle="Politique de cookies" />} />
                    {/* Custom CMS pages (admin-managed) */}
                    <Route path="/p/:slug" element={<CustomPage />} />
                    {/* 404 fallback — MUST be last */}
                    <Route path="*" element={<NotFoundPage />} />
                  </Route>
                </Routes>
              </Suspense>
            </ProvinceProvider>
          </CartProvider>
          </SiteSettingsProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
